// src/context/AuthContext.tsx
import React, {
  createContext,
  useContext,
  useMemo,
  useState,
  useCallback,
  ReactNode,
} from "react";
// Les données "runtime" (utilisées réellement dans le code)
import { DEMO_ACCOUNTS } from "../data/fakeUsers";

// Les types uniquement
import type {
  Account,
  AccountType,
  IndividualProfile,
  FamilyProfile,
  AvatarKind,
} from "../data/fakeUsers";
import type { AgeBand, ProfileRole } from "../types/profiles";
import { computeAgeBand } from "../types/profiles";
import type { AccessProfile } from "../core/accessControl";

// AuthContext centralise l'auth démo, la sélection de profil (individuel/famille/school) et les préférences (dont le dark mode).
type LoginResult = {
  ok: boolean;
  reason?: string;
};

const THEME_STORAGE_KEY = "mixmind_theme";
const PREMIUM_THEME_STORAGE_KEY = "mixmind_premium_theme_enabled";

type AddFamilyProfilePayload = {
  firstName: string;
  displayName?: string;
  birthYear?: number;
  sex?: "m" | "f";
  role?: FamilyProfile["role"];
  avatar?: AvatarKind;
  age?: number | null;
  ageBand?: AgeBand;
};

type AddFamilyProfileResult = {
  ok: boolean;
  reason?: string;
};

type AuthContextState = {
  account: Account | null;
  activeProfile: IndividualProfile | FamilyProfile | null;
  accessProfile: AccessProfile | null;

  // états dérivés
  isAuthed: boolean;
  isIndividual: boolean;
  isFamily: boolean;
  isSchool: boolean;
  isPremium: boolean;

  interfaceLabel: string;
  headerBrandLabel: string;

  // thème
  darkMode: boolean;
  setDarkMode: (value: boolean) => void;
  isPremiumTheme: boolean;
  setIsPremiumTheme: (value: boolean) => void;

  updateIndividualProfile: (updates: Partial<IndividualProfile>) => void;

  // auth
  login: (email: string, password: string) => LoginResult;
  quickLogin: (type: AccountType) => void;
  logout: () => void;

  // sélection de profil (famille / school)
  selectProfile: (id: string) => void;

  // gestion des profils multi-profils
  addFamilyProfile: (
    payload: AddFamilyProfilePayload
  ) => AddFamilyProfileResult;
  updateFamilyProfile: (
    id: string,
    updates: Partial<FamilyProfile>
  ) => void;
  removeFamilyProfile: (id: string) => void;

  // conversions de type de compte (vitrine)
  convertToFamily: () => void;
  convertToIndividual: () => void;
  convertToSchool: () => void;

  // premium (vitrine)
  activatePremium: (kind: AccountType) => void;
  cancelPremium: () => void;

  // sécurité : vérification MDP compte
  checkAccountPassword: (password: string) => boolean;

  selectFamilyProfile: (profileId: string) => void;
};
const AuthContext = createContext<AuthContextState | undefined>(undefined);

// ----------------- Helpers -----------------

function getInitialProfileForAccount(
  acc: Account
): IndividualProfile | FamilyProfile | null {
  if (acc.type === "individual") return acc.profile;
  if (acc.profiles && acc.profiles.length > 0) return acc.profiles[0];
  return null;
}

function computeInterfaceLabel(acc: Account | null): string {
  if (!acc) return "Visiteur";
  if (acc.type === "individual") {
    return acc.premium ? "Compte individuel Premium" : "Compte individuel";
  }
  if (acc.type === "family") {
    return acc.premium ? "Family Premium" : "Compte famille";
  }
  if (acc.type === "school") {
    return acc.premium ? "School Premium" : "Compte school";
  }
  return "Visiteur";
}

function computeHeaderBrandLabel(acc: Account | null): string {
  if (!acc || !acc.premium) return "MixMind";
  if (acc.type === "individual") return "MixMind Premium";
  if (acc.type === "family") return "MixMind Family Premium";
  if (acc.type === "school") return "MixMind School Premium";
  return "MixMind";
}

function normalizeProfileRole(
  role: FamilyProfile["role"] | ProfileRole | undefined,
  accountType: AccountType
): FamilyProfile["role"] {
  if (!role) {
    return accountType === "family" ? "child" : "student";
  }
  if (role === "enfant") return "child";
  if (role === "eleve") return "student";
  if (role === "enseignant") return "teacher";
  return role;
}

function mapFamilyRoleToProfileRole(
  role: FamilyProfile["role"] | undefined
): ProfileRole | undefined {
  if (!role) return undefined;
  if (role === "child") return "enfant";
  if (role === "student" || role === "eleve") return "eleve";
  if (role === "teacher" || role === "enseignant") return "enseignant";
  if (role === "parent") return "parent";
  return undefined;
}

// ----------------- Provider -----------------

type AuthProviderProps = {
  children: ReactNode;
};

export function AuthProvider({ children }: AuthProviderProps) {
  const [account, setAccount] = useState<Account | null>(null);
  const [activeProfile, setActiveProfile] = useState<
    IndividualProfile | FamilyProfile | null
  >(null);

  // préférence utilisateur brute (forcée à sombre par défaut)
  const [darkModeUserPref, setDarkModeUserPref] = useState<boolean>(() => {
    try {
      const stored = localStorage.getItem(THEME_STORAGE_KEY);
      if (stored === "light") return true; // light désactivé → on force dark
      if (stored === "dark") return true;
    } catch (e) {
      // ignore
    }
    return true;
  });

  const [isPremiumTheme, setIsPremiumTheme] = useState<boolean>(() => {
    try {
      return localStorage.getItem(PREMIUM_THEME_STORAGE_KEY) === "true";
    } catch (e) {
      return false;
    }
  });

  // mode effectif : basé sur la préférence utilisateur (verrouillé à sombre)
  const darkMode = darkModeUserPref;

  const setDarkMode = useCallback((value: boolean) => {
    if (!value) {
      window.alert("Le mode clair a été rendu indisponible pour le moment.");
      setDarkModeUserPref(true);
      return;
    }
    setDarkModeUserPref(true);
  }, []);

  // synchroniser la préférence dans le storage (toujours "dark")
  React.useEffect(() => {
    try {
      localStorage.setItem(THEME_STORAGE_KEY, "dark");
    } catch (e) {
      // ignore
    }
  }, [darkModeUserPref]);

  React.useEffect(() => {
    try {
      localStorage.setItem(
        PREMIUM_THEME_STORAGE_KEY,
        isPremiumTheme ? "true" : "false"
      );
    } catch (e) {
      // ignore
    }
  }, [isPremiumTheme]);

  // ---------- Auth de base ----------

  const login = useCallback<AuthContextState["login"]>((email, password) => {
    const found = DEMO_ACCOUNTS.find(
      (a) =>
        a.email.toLowerCase() === email.toLowerCase() && a.password === password
    );

    if (!found) {
      return { ok: false, reason: "Identifiants invalides (vitrine)." };
    }

    setAccount(found);
    setActiveProfile(getInitialProfileForAccount(found));
    setDarkModeUserPref(true);
    setIsPremiumTheme(found.premium ? true : isPremiumTheme);

    return { ok: true };
  }, [isPremiumTheme]);

  const quickLogin = useCallback<AuthContextState["quickLogin"]>((type) => {
    const found = DEMO_ACCOUNTS.find((a) => a.type === type);
    if (!found) {
      window.alert("Aucun compte de démonstration pour ce type.");
      return;
    }

    setAccount(found);
    setActiveProfile(getInitialProfileForAccount(found));
    setDarkModeUserPref(true);
    setIsPremiumTheme(found.premium ? true : isPremiumTheme);
  }, [isPremiumTheme]);

  const logout = useCallback(() => {
    setAccount(null);
    setActiveProfile(null);
    setDarkModeUserPref(true);
    setIsPremiumTheme(false);
  }, []);

  const updateIndividualProfile = useCallback<
    AuthContextState["updateIndividualProfile"]
  >(
    (updates) => {
      setAccount((prev) => {
        if (!prev || prev.type !== "individual") return prev;
        const nextProfile = { ...prev.profile, ...updates } as IndividualProfile;
        const next: Account = { ...prev, profile: nextProfile };
        setActiveProfile(nextProfile);
        return next;
      });
    },
    []
  );

  // ---------- Profils multi-profils ----------

  const selectProfile = useCallback<AuthContextState["selectProfile"]>(
    (id) => {
      if (!account || account.type === "individual") return;
      const p = account.profiles.find((prof) => prof.id === id);
      if (p) setActiveProfile(p);
    },
    [account]
  );

  const selectFamilyProfile = useCallback<
    AuthContextState["selectFamilyProfile"]
  >(
    (profileId) => {
      if (!account || account.type !== "family") return;
      const profile = account.profiles.find((p) => p.id === profileId);
      if (profile) setActiveProfile(profile);
    },
    [account]
  );

  // Ajoute un profil famille/school (vitrine) en respectant les quotas et rôles autorisés.
  const addFamilyProfile: AuthContextState["addFamilyProfile"] = useCallback(
    ({
      firstName,
      displayName,
      birthYear,
      sex,
      role,
      avatar,
      age,
      ageBand,
    }) => {
      if (!account || account.type === "individual") {
        const reason =
          "Création de profil réservée aux comptes famille ou school.";
        window.alert(reason);
        return { ok: false, reason };
      }

      const trimmed = firstName.trim();
      if (!trimmed) {
        const reason = "Le prénom est obligatoire.";
        window.alert(reason);
        return { ok: false, reason };
      }

      let blockedReason;
      const resolvedRole = normalizeProfileRole(role, account.type);
      const resolvedAgeBand = ageBand ?? computeAgeBand(age ?? null);
      const resolvedDisplayName =
        (displayName && displayName.trim()) || trimmed;

      setAccount((prev) => {
        if (!prev || prev.type === "individual") return prev;

        const profiles = prev.profiles;
        const isFamilyAccount = prev.type === "family";
        const isSchoolAccount = prev.type === "school";

        if (isFamilyAccount) {
          const maxProfiles = prev.premium ? 8 : 4;
          if (profiles.length >= maxProfiles) {
            blockedReason = "LIMIT_REACHED";
            return prev;
          }

          const parentCount = profiles.filter(
            (p) => p.role === "parent"
          ).length;
          const maxParents = 2;

          if (resolvedRole === "parent" && parentCount >= maxParents) {
            blockedReason = "TOO_MANY_PARENTS";
            return prev;
          }
        }

        if (isSchoolAccount) {
          const maxProfiles = prev.premium ? 10 : 2;
          if (profiles.length >= maxProfiles) {
            blockedReason = "LIMIT_REACHED";
            return prev;
          }

          const teacherCount = profiles.filter(
            (p) => p.role === "teacher" || p.role === "enseignant"
          ).length;
          const studentCount = profiles.filter(
            (p) => p.role === "student" || p.role === "eleve"
          ).length;

          if (!prev.premium) {
            if (
              (resolvedRole === "teacher" || resolvedRole === "enseignant") &&
              teacherCount >= 1
            ) {
              blockedReason = "TOO_MANY_TEACHERS";
              return prev;
            }
            if (
              (resolvedRole === "student" || resolvedRole === "eleve") &&
              studentCount >= 1
            ) {
              blockedReason = "TOO_MANY_STUDENTS";
              return prev;
            }
          } else {
            if (
              (resolvedRole === "teacher" || resolvedRole === "enseignant") &&
              teacherCount >= 3
            ) {
              blockedReason = "TOO_MANY_TEACHERS";
              return prev;
            }
          }
        }

        const newProfile = {
          id: "p_" + Date.now() + "_" + Math.random().toString(16).slice(2, 8),
          firstName: trimmed,
          displayName: resolvedDisplayName,
          birthYear,
          sex,
          role: resolvedRole,
          avatar: avatar || "initial",
          age: age ?? null,
          ageBand: resolvedAgeBand,
        };

        return {
          ...prev,
          profiles: [...prev.profiles, newProfile],
        };
      });

      if (blockedReason) {
        if (blockedReason === "LIMIT_REACHED") {
          window.alert("Limite de profils atteinte pour ce type de compte.");
        } else if (blockedReason === "TOO_MANY_PARENTS") {
          window.alert("Nombre maximum de profils parent atteint (2 maximum par compte famille).");
        } else if (blockedReason === "TOO_MANY_TEACHERS") {
          window.alert("Trop de profils 'enseignant' pour ce compte.");
        } else if (blockedReason === "TOO_MANY_STUDENTS") {
          window.alert("Trop de profils 'élève' pour ce compte.");
        }
        return { ok: false, reason: blockedReason };
      }

      return { ok: true };
    },
    [account]
  );

  // Met à jour un profil existant (fusion partielle, conserve l'activeProfile si besoin).
  const updateFamilyProfile = useCallback(
    (id: string, updates: Partial<FamilyProfile>) => {
      setAccount((prev) => {
        if (!prev || prev.type === "individual") return prev;
        const updatedProfiles = prev.profiles.map((p) =>
          p.id === id ? { ...p, ...updates } : p
        );
        return {
          ...prev,
          profiles: updatedProfiles,
        };
      });

      setActiveProfile((prev) => {
        if (!prev) return prev;
        if ("role" in prev && prev.id === id) {
          return { ...prev, ...updates };
        }
        return prev;
      });
    },
    []
  );

  // Supprime un profil famille/school en vérifiant qu'il reste un adulte/enseignant.
  const removeFamilyProfile = useCallback((id: string) => {
    let removedId: string | null = null;

    setAccount((prev) => {
      if (!prev || prev.type === "individual") return prev;

      const target = prev.profiles.find((p) => p.id === id);
      if (!target) return prev;

      if (prev.profiles.length <= 1) {
        window.alert("Il doit rester au moins un profil sur le compte.");
        return prev;
      }

      if (prev.type === "family") {
        const parentCount = prev.profiles.filter((p) => p.role === "parent")
          .length;
        if (target.role === "parent" && parentCount <= 1) {
          window.alert(
            'Vous ne pouvez pas supprimer le dernier profil parent/enseignant. Si vous souhaitez supprimer votre compte, rendez-vous dans la section "Gestion du compte".'
          );
          return prev;
        }
      }

      if (prev.type === "school") {
        const teacherCount = prev.profiles.filter(
          (p) => p.role === "teacher"
        ).length;
        if (target.role === "teacher" && teacherCount <= 1) {
          window.alert(
            'Vous ne pouvez pas supprimer le dernier profil parent/enseignant. Si vous souhaitez supprimer votre compte, rendez-vous dans la section "Gestion du compte".'
          );
          return prev;
        }
      }

      removedId = id;
      return {
        ...prev,
        profiles: prev.profiles.filter((p) => p.id !== id),
      };
    });

    if (removedId) {
      setActiveProfile((prev) => {
        if (!prev) return prev;
        if ("role" in prev && prev.id === removedId) {
          return null;
        }
        return prev;
      });
    }
  }, []);

  // ---------- Conversions de type de compte ----------

  const convertToFamily = useCallback(() => {
    setAccount((prev) => {
      if (!prev) return prev;
      if (prev.type === "family") return prev;

      if (prev.type === "individual") {
        const baseProfile: FamilyProfile = {
          id: `p_${Date.now()}`,
          firstName: prev.profile.firstName,
          birthYear: prev.profile.birthYear,
          sex: undefined,
          role: "parent",
          avatar: prev.profile.avatar || "initial",
        };
        const next: Account = {
          id: prev.id,
          type: "family",
          email: prev.email,
          password: prev.password,
          premium: prev.premium,
          profiles: [baseProfile],
        };
        setActiveProfile(baseProfile);
        return next;
      }

      // school -> family : on prend le premier profil comme parent
      if (prev.type === "school") {
        const first = prev.profiles[0];
        const baseProfile: FamilyProfile = {
          id: first?.id || `p_${Date.now()}`,
          firstName: first?.firstName || "Parent",
          birthYear: first?.birthYear,
          sex: first?.sex,
          role: "parent",
          avatar: first?.avatar || "initial",
        };
        const next: Account = {
          id: prev.id,
          type: "family",
          email: prev.email,
          password: prev.password,
          premium: prev.premium,
          profiles: [baseProfile],
        };
        setActiveProfile(baseProfile);
        return next;
      }

      return prev;
    });
  }, []);

  const convertToIndividual = useCallback(() => {
    setAccount((prev) => {
      if (!prev) return prev;
      if (prev.type === "individual") return prev;

      const sourceProfile =
        prev.type === "family" ? prev.profiles[0] : prev.profiles[0]; // school → on prend aussi le premier

      const baseProfile: IndividualProfile = {
        id: sourceProfile?.id || `u_${Date.now()}`,
        firstName: sourceProfile?.firstName || "Utilisateur",
        birthYear: sourceProfile?.birthYear,
        avatar: sourceProfile?.avatar || "initial",
      };

      const next: Account = {
        id: prev.id,
        type: "individual",
        email: prev.email,
        password: prev.password,
        premium: prev.premium,
        profile: baseProfile,
      };
      setActiveProfile(baseProfile);
      return next;
    });
  }, []);

  const convertToSchool = useCallback(() => {
    setAccount((prev) => {
      if (!prev) return prev;
      if (prev.type === "school") return prev;

      if (prev.type === "individual") {
        const teacher: FamilyProfile = {
          id: `p_${Date.now()}`,
          firstName: prev.profile.firstName || "Enseignant",
          birthYear: prev.profile.birthYear,
          sex: undefined,
          role: "teacher",
          avatar: prev.profile.avatar || "man",
        };
        const next: Account = {
          id: prev.id,
          type: "school",
          email: prev.email,
          password: prev.password,
          premium: prev.premium,
          profiles: [teacher],
        };
        setActiveProfile(teacher);
        return next;
      }

      if (prev.type === "family") {
        const parent = prev.profiles.find((p) => p.role === "parent");
        const teacher: FamilyProfile = {
          id: parent?.id || `p_${Date.now()}`,
          firstName: parent?.firstName || "Enseignant",
          birthYear: parent?.birthYear,
          sex: parent?.sex,
          role: "teacher",
          avatar: parent?.avatar || "man",
        };
        const next: Account = {
          id: prev.id,
          type: "school",
          email: prev.email,
          password: prev.password,
          premium: prev.premium,
          profiles: [teacher],
        };
        setActiveProfile(teacher);
        return next;
      }

      return prev;
    });
  }, []);

  // ---------- Premium ----------

  const activatePremium = useCallback<AuthContextState["activatePremium"]>(
    (kind) => {
      if (!account) {
        window.alert(
          "Pour activer un plan Premium, connecte-toi d'abord (ou utilise un compte de démonstration)."
        );
        return;
      }

      // Vérifie que le type de compte correspond bien au plan
      if (account.type !== kind) {
        if (kind === "family") {
          window.alert(
            "Le plan Family Premium est réservé aux comptes famille.\nVa dans Profil → Gestion du compte pour passer en compte famille."
          );
        } else if (kind === "school") {
          window.alert(
            "Le plan School Premium est réservé aux comptes school.\nVa dans Profil → Gestion du compte pour passer en compte school."
          );
        } else {
          window.alert(
            "Le plan Premium individuel est réservé aux comptes individuels."
          );
        }
        return;
      }

      if (account.premium) {
        window.alert("Ce compte est déjà en Premium (vitrine).");
        return;
      }

      setAccount((prev) => (prev ? { ...prev, premium: true } : prev));
      setDarkModeUserPref(true); // Premium → dark mode on
      window.alert("Plan Premium activé (vitrine).");
    },
    [account]
  );

  const cancelPremium = useCallback(() => {
    setAccount((prev) => (prev ? { ...prev, premium: false } : prev));
    // on laisse la préférence utilisateur décider ensuite
  }, []);

  React.useEffect(() => {
    if (account?.premium) {
      setIsPremiumTheme(true);
    } else {
      setIsPremiumTheme(false);
    }
  }, [account]);

  const checkAccountPassword = useCallback(
    (password: string) => {
      if (!account) return false;
      return account.password === password;
    },
    [account]
  );

  // ---------- valeurs dérivées ----------

  const isAuthed = !!account;
  const isIndividual = account?.type === "individual";
  const isFamily = account?.type === "family";
  const isSchool = account?.type === "school";
  const isPremium = !!account?.premium;

  const interfaceLabel = computeInterfaceLabel(account);
  const headerBrandLabel = computeHeaderBrandLabel(account);

  const accessProfile = useMemo<AccessProfile | null>(() => {
    if (!activeProfile) return null;

    // Profil familial/school : on peut lire le rôle/ageBand directement
    if ("role" in activeProfile) {
      const role = mapFamilyRoleToProfileRole(activeProfile.role);
      const ageBandFromProfile = activeProfile.ageBand;
      const ageBandResolved =
        ageBandFromProfile ??
        (activeProfile.age !== undefined ? computeAgeBand(activeProfile.age) : undefined);
      return {
        role,
        ageBand: ageBandResolved ?? undefined,
      };
    }

    // Profil individuel : pas de rôle, ageBand non calculé pour l'instant
    return {
      role: undefined,
      ageBand: undefined,
    };
  }, [activeProfile]);

  const value = useMemo<AuthContextState>(
    () => ({
      account,
      activeProfile,
      accessProfile,
      isAuthed,
      isIndividual,
      isFamily,
      isSchool,
      isPremium,
      interfaceLabel,
      headerBrandLabel,
      darkMode,
      setDarkMode,
      isPremiumTheme,
      setIsPremiumTheme,
      login,
      quickLogin,
      logout,
      selectProfile,
      selectFamilyProfile,
      addFamilyProfile,
      updateFamilyProfile,
      removeFamilyProfile,
      convertToFamily,
      convertToIndividual,
      convertToSchool,
      activatePremium,
      cancelPremium,
      checkAccountPassword,
      updateIndividualProfile,
    }),
    [
      account,
      activeProfile,
      accessProfile,
      isAuthed,
      isIndividual,
      isFamily,
      isSchool,
      isPremium,
      interfaceLabel,
      headerBrandLabel,
      darkMode,
      setDarkMode,
      isPremiumTheme,
      setIsPremiumTheme,
      login,
      quickLogin,
      logout,
      selectProfile,
      selectFamilyProfile,
      addFamilyProfile,
      updateFamilyProfile,
      removeFamilyProfile,
      convertToFamily,
      convertToIndividual,
      convertToSchool,
      activatePremium,
      cancelPremium,
      checkAccountPassword,
      updateIndividualProfile,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// ----------------- Hook -----------------

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used inside <AuthProvider>");
  }
  return ctx;
}
